/**
 * プラットフォーム設定の境界アダプタ。
 *
 * API（`GET /ops/settings`）は `system_settings` を **キー・値の平坦なリスト**で返し、
 * 更新も `PUT /ops/settings/:key` の 1 キー単位でしか受け付けない（監査を
 * キー単位で残すため）。一方、設定画面はタブごとにまとまった入れ子の
 * オブジェクトを編集する。両者の写像をここ 1 か所に閉じる。
 *
 * 原則:
 *  - API を正とする。キー名（`general.brandName` 等）がこのファイルの唯一の真実。
 *  - 未設定のキーは **ゼロ値**（空文字 / 既定数値 / []）で埋める。undefined を
 *    画面へ流さない（入力欄が uncontrolled になって編集不能になるため）。
 *  - 保存は「変更されたキーだけ」を PUT する。触っていないキーを毎回上書きすると、
 *    別タブ・別運営者の変更を巻き戻してしまう。
 */
import { request, OPS_PREFIX } from './api.js';
import type { OpsSettings } from '../types/api.js';

/** `GET /ops/settings` の生の形。 */
export interface ApiSettingsList {
  items: Array<{
    key: string;
    /** 秘密扱いのキーは `'[masked]'` が入る。編集時に送り返さないこと。 */
    value: unknown;
    isSecret: boolean;
    description: string | null;
    updatedAt: string;
  }>;
  total: number;
}

/** 秘密扱いのキーで API が返すマスク値。 */
const MASKED = '[masked]';

/** 設定が 1 件も無い環境（初期状態）でも画面が描けるようにする既定値。 */
const DEFAULTS: OpsSettings = {
  general: { brandName: '', supportEmail: '', supportUrl: null },
  acme: { directoryUrl: '', contactEmail: '', eabKid: null, renewBeforeDays: 30 },
  notification: { alertEmails: [], slackWebhookConfigured: false, events: {} },
  webmail: {
    baseUrl: '',
    adminApiUrl: '',
    apiKeyMasked: null,
    lastSyncAt: null,
    syncState: 'pending',
  },
  rollout: { defaultSoakMinutes: { canary: 60, ring1: 120 }, maxUnavailablePct: 10 },
};

/**
 * 入れ子のパスと `system_settings.key` の対応表。
 * ここに無いキーは画面に出さない（API 側に増えても壊れない）。
 */
const KEY_MAP = {
  'general.brandName': (s: OpsSettings) => s.general.brandName,
  'general.supportEmail': (s: OpsSettings) => s.general.supportEmail,
  'general.supportUrl': (s: OpsSettings) => s.general.supportUrl,
  'acme.directoryUrl': (s: OpsSettings) => s.acme.directoryUrl,
  'acme.contactEmail': (s: OpsSettings) => s.acme.contactEmail,
  'acme.eabKid': (s: OpsSettings) => s.acme.eabKid,
  'acme.renewBeforeDays': (s: OpsSettings) => s.acme.renewBeforeDays,
  'notification.alertEmails': (s: OpsSettings) => s.notification.alertEmails,
  'notification.events': (s: OpsSettings) => s.notification.events,
  'webmail.baseUrl': (s: OpsSettings) => s.webmail.baseUrl,
  'webmail.adminApiUrl': (s: OpsSettings) => s.webmail.adminApiUrl,
  'rollout.defaultSoakMinutes': (s: OpsSettings) => s.rollout.defaultSoakMinutes,
  'rollout.maxUnavailablePct': (s: OpsSettings) => s.rollout.maxUnavailablePct,
} as const;

type SettingKey = keyof typeof KEY_MAP;

/* ------------------------------------------------------------------ 読み */

export function toOpsSettings(res: ApiSettingsList): OpsSettings {
  const byKey = new Map(res.items.map((i) => [i.key, i]));
  const str = (key: string, fallback: string): string => {
    const v = byKey.get(key)?.value;
    return typeof v === 'string' ? v : fallback;
  };
  const nullableStr = (key: string): string | null => {
    const v = byKey.get(key)?.value;
    return typeof v === 'string' && v.length > 0 ? v : null;
  };
  const num = (key: string, fallback: number): number => {
    const v = byKey.get(key)?.value;
    return typeof v === 'number' ? v : fallback;
  };

  const soak = byKey.get('rollout.defaultSoakMinutes')?.value;
  const events = byKey.get('notification.events')?.value;
  const alertEmails = byKey.get('notification.alertEmails')?.value;
  const webmailKey = byKey.get('webmail.apiKey');

  return {
    general: {
      brandName: str('general.brandName', DEFAULTS.general.brandName),
      supportEmail: str('general.supportEmail', DEFAULTS.general.supportEmail),
      supportUrl: nullableStr('general.supportUrl'),
    },
    acme: {
      directoryUrl: str('acme.directoryUrl', DEFAULTS.acme.directoryUrl),
      contactEmail: str('acme.contactEmail', DEFAULTS.acme.contactEmail),
      eabKid: nullableStr('acme.eabKid'),
      renewBeforeDays: num('acme.renewBeforeDays', DEFAULTS.acme.renewBeforeDays),
    },
    notification: {
      alertEmails: Array.isArray(alertEmails) ? (alertEmails as string[]) : [],
      // Webhook URL は秘密扱いなのでマスクされて返る。設定済みか否かだけ分かればよい。
      slackWebhookConfigured: byKey.has('notification.slackWebhookUrl'),
      events: isRecordOfBoolean(events) ? events : {},
    },
    webmail: {
      baseUrl: str('webmail.baseUrl', DEFAULTS.webmail.baseUrl),
      adminApiUrl: str('webmail.adminApiUrl', DEFAULTS.webmail.adminApiUrl),
      apiKeyMasked: webmailKey === undefined ? null : MASKED,
      lastSyncAt: nullableStr('webmail.lastSyncAt'),
      // 同期状態は設定ではなく実行時の状態で、設定 API は持っていない。
      // 実際の差分は `GET /ops/webmail/drift`（画面の「差分を確認」）が返すので、
      // ここは「未判定」を意味する pending を固定で出す。
      syncState: 'pending',
    },
    rollout: {
      defaultSoakMinutes: isSoak(soak) ? soak : DEFAULTS.rollout.defaultSoakMinutes,
      maxUnavailablePct: num('rollout.maxUnavailablePct', DEFAULTS.rollout.maxUnavailablePct),
    },
  };
}

/* ------------------------------------------------------------------ 書き */

/**
 * 変更されたキーだけを `PUT /ops/settings/:key` で保存する。
 * 1 キーでも失敗したら例外を投げる（部分保存を成功扱いにしない）。
 */
export async function saveOpsSettings(before: OpsSettings, after: OpsSettings): Promise<void> {
  const changed = (Object.keys(KEY_MAP) as SettingKey[]).filter(
    (key) => JSON.stringify(KEY_MAP[key](before)) !== JSON.stringify(KEY_MAP[key](after)),
  );

  for (const key of changed) {
    await request<{ ok: true }>(`${OPS_PREFIX}/settings/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: { value: KEY_MAP[key](after) },
    });
  }
}

/* ---------------------------------------------------------------- 型ガード */

function isRecordOfBoolean(v: unknown): v is Record<string, boolean> {
  return (
    typeof v === 'object' &&
    v !== null &&
    !Array.isArray(v) &&
    Object.values(v).every((x) => typeof x === 'boolean')
  );
}

function isSoak(v: unknown): v is { canary: number; ring1: number } {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { canary?: unknown }).canary === 'number' &&
    typeof (v as { ring1?: unknown }).ring1 === 'number'
  );
}
